import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { PDFUploadCard, AIProvider } from '@/components/validation/PDFUploadCard';
import {
  TicketExtractionResults,
  TicketExtractionResult,
  TicketLineItem,
} from '@/components/ticket/TicketExtractionResults';
import { priceBookItemService, ticketService, ticketItemService, type ServiceContext } from '@/services';
import { extractService } from '@/api/extract';
import { getDefaultStatusId } from '@/hooks/useDefaultStatus';
import type { PriceBookItem } from '@/services';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  vendorId: string | null;
  priceBookId: string | null;
  companyId: string;
  userId: string;
  onTicketSaved?: () => void;
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64Data = result.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function UploadFieldTicketDialog({
  open,
  onOpenChange,
  invoiceId,
  vendorId,
  priceBookId,
  companyId,
  userId,
  onTicketSaved,
}: Props) {
  // Upload state
  const [file, setFile] = useState<File | null>(null);
  const [aiProvider, setAIProvider] = useState<AIProvider>('openai');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [extractionResults, setExtractionResults] = useState<TicketExtractionResult | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Price book items for matching
  const [priceBookItems, setPriceBookItems] = useState<PriceBookItem[]>([]);

  // Fetch price book items when dialog opens
  useEffect(() => {
    if (open && priceBookId) {
      priceBookItemService.listByPriceBook(priceBookId).then((result) => {
        if (!result.error && result.data) {
          setPriceBookItems(result.data);
        }
      });
    }
  }, [open, priceBookId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setFile(null);
      setAIProvider('openai');
      setIsProcessing(false);
      setProcessingStatus('');
      setError(null);
      setExtractionResults(null);
      setIsSaving(false);
      setPriceBookItems([]);
    }
  }, [open]);

  const matchTicketItemsToPriceBook = useCallback(
    (lineItems: TicketLineItem[], pbItems: PriceBookItem[]): TicketLineItem[] => {
      if (!pbItems.length) return lineItems;

      return lineItems.map((item) => {
        if (!item.item_code) return item;

        const match = pbItems.find(
          (pbItem) => pbItem.itemcode.toLowerCase() === item.item_code?.toLowerCase(),
        );

        return {
          ...item,
          price_book_item_id: match?.id || undefined,
        };
      });
    },
    [],
  );

  const handleExtract = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);
    setProcessingStatus('Reading PDF file...');

    try {
      const base64 = await fileToBase64(file);
      const providerLabel = 'OpenAI';
      setProcessingStatus(`Extracting data with ${providerLabel}...`);

      const data = await extractService.extractTicket<TicketExtractionResult>({
        pdfBase64: base64,
        aiProvider,
      });

      if (!data.success) throw new Error(data.error || 'Extraction failed');

      let extractedResult = data;
      if (priceBookItems.length > 0) {
        const matchedItems = matchTicketItemsToPriceBook(extractedResult.data.line_items, priceBookItems);
        extractedResult = {
          ...extractedResult,
          data: { ...extractedResult.data, line_items: matchedItems },
        };
      }

      setExtractionResults(extractedResult);
      toast.success('Ticket extraction complete');
    } catch (err) {
      console.error('Ticket extraction error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error('Extraction failed', { description: errorMessage });
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
    }
  };

  const handleClear = () => {
    setFile(null);
    setExtractionResults(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!extractionResults) return;

    setIsSaving(true);
    try {
      const { data: extractedData } = extractionResults;
      const context: ServiceContext = { companyId, userId };

      // 1. Upload PDF to storage
      let source_file_path: string | null = null;
      let source_filename: string | null = null;

      if (file) {
        source_filename = file.name;
        const filePath = `${companyId}/${Date.now()}_${file.name}`;
        const { error: uploadError } = await supabase.storage.from('ticket-files').upload(filePath, file);

        if (uploadError) {
          console.error('File upload error:', uploadError);
        } else {
          source_file_path = filePath;
        }
      }

      // 2. Get default status_id
      const defaultStatusId = await getDefaultStatusId('invoice'); // tickets use same entity type

      // 3. Insert ticket header
      const ticketResult = await ticketService.create(
        {
          vendor_id: vendorId,
          ticket_number: extractedData.ticket_number || null,
          ticket_date: extractedData.ticket_date || null,
          subtotal: extractedData.subtotal || 0,
          tax_amount: extractedData.tax_amount || 0,
          total_amount: extractedData.total_amount,
          currency: extractedData.currency || 'USD',
          notes: extractedData.notes || null,
          extraction_method: extractionResults.extraction_method,
          extraction_confidence: extractionResults.confidence,
          ai_provider: aiProvider,
          source_file_path,
          source_filename,
          status_id: defaultStatusId,
          price_book_id: priceBookId,
          invoice_id: invoiceId,
        },
        context,
      );

      if (ticketResult.error || !ticketResult.data) {
        throw ticketResult.error || new Error('Failed to create ticket');
      }

      const ticket = ticketResult.data;

      // 4. Insert ticket items
      if (extractedData.line_items.length > 0) {
        const ticketItems = extractedData.line_items.map((item) => ({
          ticket_id: ticket.id,
          sequence: item.sequence,
          item_code: item.item_code || null,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          uom: item.uom || null,
          discount: item.discount ?? null,
          line_total: item.line_total,
          price_book_item_id: item.price_book_item_id || null,
        }));

        const itemsResult = await ticketItemService.createBatch(ticketItems, context);
        if (itemsResult.error) throw itemsResult.error;
      }

      toast.success('Ticket saved', {
        description: `Ticket ${extractedData.ticket_number || 'N/A'} saved successfully.`,
      });

      onOpenChange(false);
      onTicketSaved?.();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Failed to save ticket',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Field Ticket</DialogTitle>
          <DialogDescription>
            Upload a field ticket PDF to extract and save against this invoice.
          </DialogDescription>
        </DialogHeader>

        {!extractionResults ? (
          <PDFUploadCard
            title="Upload Field Ticket"
            description="Upload a field ticket PDF"
            file={file}
            setFile={setFile}
            aiProvider={aiProvider}
            setAIProvider={setAIProvider}
            isProcessing={isProcessing}
            processingStatus={processingStatus}
            error={error}
            setError={setError}
            onExtract={handleExtract}
            onClear={handleClear}
          />
        ) : (
          <TicketExtractionResults
            results={extractionResults}
            onClear={handleClear}
            onSave={handleSave}
            isSaving={isSaving}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
